// LoginPage.js — Branded login page with StarLIMS logo and brand colours
// Place in: reporting-frontend/src/pages/LoginPage.js
//
// REQUIRES: copy StarLIMS_rgb.jpg to reporting-frontend/public/starlims-logo.jpg

import { useState } from 'react';
import { msalInstance } from '../msalInstance';
import { loginRequest } from '../authConfig';
import api from '../api';

export default function LoginPage({ onOtpToken }) {
  const [email,   setEmail]   = useState('');
  const [step,    setStep]    = useState('email');
  const [code,    setCode]    = useState('');
  const [error,   setError]   = useState('');
  const [loading, setLoading] = useState(false);
  const [sent,    setSent]    = useState(false);

  const isStarLIMS = email.toLowerCase().trim().endsWith('@starlims.com');

  const handleEmailSubmit = async () => {
    const trimmed = email.trim();
    if (!trimmed) return;
    setError('');
    setLoading(true);
    if (isStarLIMS) {
      msalInstance.loginRedirect(loginRequest);
      return;
    }
    try {
      await api.post('/api/v1/auth/request-otp', { email: trimmed });
      setSent(true);
      setStep('otp');
    } catch {
      setError('Something went wrong. Please try again.');
      setLoading(false);
    }
  };

  const handleOtpSubmit = async () => {
    if (code.length !== 6) { setError('Please enter the 6-digit code.'); return; }
    setError('');
    setLoading(true);
    try {
      const res = await api.post('/api/v1/auth/verify-otp', { email: email.trim(), code: code.trim() });
      localStorage.setItem('otp_token', res.data.token);
      onOtpToken(res.data.token);
    } catch (err) {
      setError(err?.response?.data?.detail || 'Invalid or expired code. Please try again.');
      setLoading(false);
    }
  };

  const handleResend = async () => {
    setError(''); setCode(''); setSent(false); setLoading(true);
    try {
      await api.post('/api/v1/auth/request-otp', { email: email.trim() });
      setSent(true);
    } catch {
      setError('Could not resend. Please try again.');
    } finally { setLoading(false); }
  };

  return (
    <div style={s.shell}>

      {/* ── Left brand panel ── */}
      <div style={s.left}>
        <div style={s.leftInner}>

          {/* Full logo — white version via CSS filter */}
          <img
            src="/starlims-logo.jpg"
            alt="StarLIMS"
            style={{
              height: 48,
              width: 'auto',
              mixBlendMode: 'screen',   // removes white JPG background on dark panel
              marginBottom: 28,
              display: 'block',
            }}
          />

          <p style={s.tagline}>Cloud Usage Reporting</p>
          <p style={s.desc}>
            Monitor Usage, track burn-down progress.
          </p>
          <div style={s.pills}>
            {['Usage analytics', 'Licence burn-down', 'Cost visibility', 'ETL health'].map(t => (
              <span key={t} style={s.pill}>{t}</span>
            ))}
          </div>
        </div>
        <div style={s.leftFooter}>
          &copy; {new Date().getFullYear()} StarLIMS. All rights reserved.
        </div>
      </div>

      {/* ── Right login card ── */}
      <div style={s.right}>
        <div style={s.card}>

          {/* Logo in card — full colour, constrained height */}
          <img
            src="/starlims-logo.jpg"
            alt="StarLIMS"
            style={{
              height: 36,
              width: 'auto',
              display: 'block',
              marginBottom: 28,
              objectFit: 'contain',
            }}
          />

          {step === 'email' ? (
            <>
              <h1 style={s.heading}>Sign in</h1>
              <p style={s.sub}>Enter your work email to continue.</p>

              <label style={s.label}>Email address</label>
              <input
                style={s.input}
                type="email"
                placeholder="you@example.com"
                value={email}
                autoFocus
                onChange={e => setEmail(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && handleEmailSubmit()}
              />

              {email.trim() && (
                <div style={s.hint}>
                  <span style={{ ...s.hintDot, background: isStarLIMS ? '#00AEEF' : '#3D1A8E' }} />
                  {isStarLIMS
                    ? 'Sign in with StarLIMS SSO'
                    : 'A one-time code will be sent to this address'}
                </div>
              )}

              <button
                style={{ ...s.btn, opacity: (loading || !email.trim()) ? 0.5 : 1 }}
                onClick={handleEmailSubmit}
                disabled={loading || !email.trim()}
              >
                {loading ? 'Please wait…' : 'Continue'}
              </button>
            </>
          ) : (
            <>
              <h1 style={s.heading}>Check your email</h1>
              <p style={s.sub}>
                {sent
                  ? <> We sent a 6-digit code to <strong>{email}</strong>. It expires in 15 minutes.</>
                  : 'Enter your access code below.'}
              </p>

              <label style={s.label}>Access code</label>
              <input
                style={{ ...s.input, letterSpacing: '0.3em', fontSize: 24, textAlign: 'center', fontWeight: 500 }}
                type="text"
                inputMode="numeric"
                placeholder="000000"
                maxLength={6}
                value={code}
                autoFocus
                onChange={e => setCode(e.target.value.replace(/\D/g, ''))}
                onKeyDown={e => e.key === 'Enter' && handleOtpSubmit()}
              />

              <button
                style={{ ...s.btn, opacity: (loading || code.length !== 6) ? 0.5 : 1 }}
                onClick={handleOtpSubmit}
                disabled={loading || code.length !== 6}
              >
                {loading ? 'Verifying…' : 'Sign in'}
              </button>

              <div style={s.links}>
                <button style={s.linkBtn} onClick={handleResend} disabled={loading}>
                  Resend code
                </button>
                <span style={{ color: '#ccc' }}>·</span>
                <button style={s.linkBtn} onClick={() => { setStep('email'); setCode(''); setError(''); }}>
                  Use a different email
                </button>
              </div>
            </>
          )}

          {error && <div style={s.error}>{error}</div>}

          <p style={s.footer}>Access is restricted to registered users only.</p>
        </div>
      </div>
    </div>
  );
}

const BRAND_PURPLE = '#3D1A8E';
const BRAND_BLUE   = '#00AEEF';

const s = {
  shell: {
    display: 'flex',
    minHeight: '100vh',
    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
  },
  left: {
    flex: '1 1 45%',
    background: `linear-gradient(145deg, ${BRAND_PURPLE} 0%, #2a1266 60%, #1a0a4a 100%)`,
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'space-between',
    padding: '48px 52px',
    minWidth: 0,
  },
  leftInner: { maxWidth: 400 },
  tagline: {
    color: BRAND_BLUE,
    fontSize: 13,
    fontWeight: 600,
    letterSpacing: '0.08em',
    textTransform: 'uppercase',
    margin: '0 0 16px',
  },
  desc: {
    color: 'rgba(255,255,255,0.75)',
    fontSize: 15,
    lineHeight: 1.7,
    margin: '0 0 28px',
  },
  pills: { display: 'flex', flexWrap: 'wrap', gap: 8 },
  pill: {
    background: 'rgba(255,255,255,0.1)',
    border: '1px solid rgba(255,255,255,0.2)',
    color: 'rgba(255,255,255,0.85)',
    borderRadius: 20,
    padding: '4px 14px',
    fontSize: 12,
    fontWeight: 500,
  },
  leftFooter: {
    color: 'rgba(255,255,255,0.35)',
    fontSize: 12,
  },
  right: {
    flex: '1 1 55%',
    background: '#f7f7f5',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '40px 24px',
  },
  card: {
    background: '#fff',
    borderRadius: 14,
    boxShadow: '0 2px 24px rgba(0,0,0,0.07)',
    padding: '40px 44px 36px',
    width: '100%',
    maxWidth: 400,
  },
  heading: {
    fontSize: 22,
    fontWeight: 600,
    color: '#1a1a1a',
    margin: '0 0 6px',
  },
  sub: {
    fontSize: 14,
    color: '#666',
    margin: '0 0 24px',
    lineHeight: 1.5,
  },
  label: {
    display: 'block',
    fontSize: 12,
    fontWeight: 500,
    color: '#555',
    marginBottom: 6,
    letterSpacing: '0.02em',
  },
  input: {
    width: '100%',
    padding: '10px 13px',
    fontSize: 14,
    border: '1.5px solid #e0e0e0',
    borderRadius: 8,
    marginBottom: 14,
    boxSizing: 'border-box',
    color: '#1a1a1a',
    outline: 'none',
  },
  hint: {
    display: 'flex',
    alignItems: 'center',
    gap: 7,
    fontSize: 12,
    color: '#666',
    marginBottom: 14,
    marginTop: -6,
  },
  hintDot: {
    width: 7,
    height: 7,
    borderRadius: '50%',
    flexShrink: 0,
  },
  btn: {
    width: '100%',
    padding: '11px 0',
    background: BRAND_PURPLE,
    color: '#fff',
    border: 'none',
    borderRadius: 8,
    fontSize: 14,
    fontWeight: 600,
    cursor: 'pointer',
    marginBottom: 14,
    letterSpacing: '0.01em',
  },
  links: {
    display: 'flex',
    gap: 10,
    justifyContent: 'center',
    alignItems: 'center',
    marginBottom: 4,
  },
  linkBtn: {
    background: 'none',
    border: 'none',
    color: BRAND_PURPLE,
    fontSize: 12,
    cursor: 'pointer',
    textDecoration: 'underline',
    padding: 0,
    fontFamily: 'inherit',
  },
  error: {
    background: '#fef2f2',
    border: '1px solid #fecaca',
    borderRadius: 6,
    color: '#991b1b',
    fontSize: 12,
    padding: '8px 12px',
    marginTop: 4,
  },
  footer: {
    fontSize: 11,
    color: '#aaa',
    textAlign: 'center',
    marginTop: 24,
    marginBottom: 0,
  },
};
